import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import type { MediaVersionDto } from '@roadfootage/api-client'
import { MediaVersion, VirusScanStatus } from '@roadfootage/api-client'
import { useApi } from '../../composables/api'

/** Staff "Download..." flow for one user file: pick a version, confirm before an unscanned original, get a link. */
export const useMediaDownloadStore = defineStore('media-download', () => {
  const { adminUserFilesApi } = useApi()

  const userFileId = ref<string | null>(null)
  const fileName = ref('')
  const virusScanStatus = ref<VirusScanStatus | null>(null)
  const versions = ref<MediaVersionDto[]>([])
  const selected = ref<MediaVersion | null>(null)
  const needsConfirm = ref(false)
  const link = ref<string | null>(null)
  const loading = ref(false)

  // The server decides which versions need acknowledging (an original that is not Clean).
  const selectedRequiresAck = computed(
    () => versions.value.find((v) => v.version === selected.value)?.requiresAcknowledgement ?? false
  )

  function reset() {
    userFileId.value = null
    fileName.value = ''
    virusScanStatus.value = null
    versions.value = []
    selected.value = null
    needsConfirm.value = false
    link.value = null
    loading.value = false
  }

  async function open(id: string, name: string) {
    reset()
    userFileId.value = id
    fileName.value = name
    loading.value = true
    try {
      const response = await adminUserFilesApi.adminUserFilesListVersionsById({ id })
      // Ignore a late answer for a dialog that was closed or reopened for another file.
      if (userFileId.value !== id) return
      virusScanStatus.value = response.virusScanStatus ?? null
      versions.value = response.versions ?? []
    } catch {
      // The global API middleware already shows the error toast.
    } finally {
      if (userFileId.value === id) loading.value = false
    }
  }

  function select(version: MediaVersion) {
    selected.value = version
    needsConfirm.value = false
    link.value = null
  }

  // The only place that calls the download-url endpoint.
  async function issue(acknowledgeRisk: boolean) {
    const id = userFileId.value
    const version = selected.value
    if (!id || !version) return
    loading.value = true
    needsConfirm.value = false
    link.value = null
    try {
      const response = await adminUserFilesApi.adminUserFilesCreateDownloadUrlById({
        id,
        mediaDownloadRequest: { version, acknowledgeRisk }
      })
      // Ignore the answer if the staff member changed their pick while it was in flight.
      if (userFileId.value === id && selected.value === version) link.value = response.url ?? null
    } catch {
      // The global API middleware already shows the error toast.
    } finally {
      if (userFileId.value === id && selected.value === version) loading.value = false
    }
  }

  async function requestLink() {
    if (selectedRequiresAck.value) {
      needsConfirm.value = true
      link.value = null
      return
    }
    await issue(false)
  }

  async function confirmRisk() {
    if (!needsConfirm.value || !selectedRequiresAck.value) return
    await issue(true)
  }

  return {
    userFileId,
    fileName,
    virusScanStatus,
    versions,
    selected,
    needsConfirm,
    link,
    loading,
    selectedRequiresAck,
    open,
    select,
    requestLink,
    confirmRisk,
    reset
  }
})
